import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import BrandLogo from './BrandLogo';
import { useWorkspaceOptional } from '@/contexts/WorkspaceContext';
import { can, PermissionKey } from '@/lib/permissions';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { logout } from '@/lib/api';
import { useAccessToken } from '@/lib/session';

interface NavbarProps {
  // 'scene' is the paper rail used by the public marketing pages (see src/styles/scene.css).
  variant?: 'default' | 'scene';
}

export default function Navbar({ variant = 'default' }: NavbarProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const isAuthenticated = !!useAccessToken();

  useEffect(() => {
    if (variant !== 'scene') return;
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [variant]);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
  };

  const handleLogout = () => {
    setMobileMenuOpen(false);
    // Leave the protected page first. Clearing the session while still on it would have its guard
    // redirect to /login, racing this navigation to the home page.
    navigate('/');
    void logout();
  };

  const workspace = useWorkspaceOptional();
  const { data: me } = useCurrentUser();
  const isSuperAdmin = !!me?.user?.isSuperAdmin;
  const activeId = useParams().workspaceId || localStorage.getItem('workspaceId');
  const wsLink = (page: string) => (activeId ? `/w/${activeId}/${page}` : '/app');
  // Permissions for the active workspace. Inside a workspace we read the live context; on
  // workspace-agnostic pages (Profile/Support) the context is null, so fall back to the
  // permissions cached by WorkspaceLayout. Without this the guard fails OPEN and shows
  // every link regardless of role.
  let cachedPermissions: string[] | null = null;
  try { cachedPermissions = JSON.parse(localStorage.getItem('workspacePermissions') || 'null'); } catch { /* ignore malformed cache */ }
  const permissions = workspace?.permissions ?? cachedPermissions;
  // Super admins are never gated; otherwise require the capability (fail closed when unknown).
  const allow = (perm?: PermissionKey) => !perm || isSuperAdmin || can(permissions, perm);

  const workspaceLinks: { to: string; label: string; perm?: PermissionKey }[] = [
    { to: wsLink('dashboard'), label: t('nav.dashboard') },
    { to: wsLink('campaigns'), label: t('nav.campaigns', 'Campaigns'), perm: 'campaign:read' },
    { to: wsLink('contacts'), label: t('nav.contacts', 'Contacts'), perm: 'contacts:read' },
    { to: wsLink('users'), label: t('nav.users'), perm: 'members:read' },
    { to: wsLink('whatsapp-templates'), label: 'Templates', perm: 'templates:read' },
    { to: wsLink('billing'), label: t('nav.billing', 'Billing'), perm: 'billing:read' },
  ];

  const adminLinks = [
    { to: '/admin/plans', label: 'Subscription Plans' },
    { to: '/admin/users', label: 'Users' },
    { to: '/admin/usage', label: 'Super-Admin Usage' },
    { to: '/admin/generation-pricing', label: 'AI Generation Pricing' },
  ];

  const navLinks = isAuthenticated
    ? [
      ...workspaceLinks.filter((link) => allow(link.perm)).map(({ to, label }) => ({ to, label })),
      { to: '/profile', label: 'Profile' },
      { to: '/support', label: t('nav.support') },
    ]
    : [
      { to: '/', label: t('nav.home') },
      { to: '/pricing', label: t('nav.pricing') },
      { to: '/how-it-works', label: t('nav.how_it_works') },
      { to: '/support', label: t('nav.support') },
      { to: '/about', label: t('nav.about', 'About') },
    ];

  if (variant === 'scene') {
    const isEn = i18n.language !== 'ar';
    return (
      <nav className="rail" data-scrolled={scrolled} data-open={mobileMenuOpen}>
        <div className="wrap flex items-center justify-between gap-6 h-[76px]">
          <Link to="/" className="flex items-center shrink-0">
            <BrandLogo showTagline size="lg" className="h-14" />
          </Link>

          <div className="hidden lg:flex items-center gap-7">
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to} className="rail__link" aria-current={pathname === link.to ? 'page' : undefined}>
                {link.label}
              </Link>
            ))}
            {isSuperAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger className="rail__link flex items-center gap-1 focus:outline-none">
                  Admin
                  <ChevronDown className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {adminLinks.map((link) => (
                    <DropdownMenuItem key={link.to} asChild>
                      <Link to={link.to}>{link.label}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <div className="hidden lg:flex items-center gap-5">
            <button type="button" className="rail__lang" onClick={toggleLanguage} title="Toggle Language">
              {isEn ? <><b>EN</b> / ع</> : <>EN / <b>ع</b></>}
            </button>
            {isAuthenticated ? (
              <button type="button" className="btn-line" onClick={handleLogout}>{t('nav.logout')}</button>
            ) : (
              <>
                <Link to="/login" className="rail__link">{t('nav.login')}</Link>
                <button type="button" className="btn-ink" onClick={() => navigate('/signup')}>{t('nav.signup')}</button>
              </>
            )}
          </div>

          <button
            type="button"
            className="lg:hidden p-2 -me-2"
            aria-label="Menu"
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="wrap lg:hidden pb-8 grid gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rail__link py-3 border-b border-[var(--hairline)]"
                aria-current={pathname === link.to ? 'page' : undefined}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            {isSuperAdmin && adminLinks.map((link) => (
              <Link key={link.to} to={link.to} className="rail__link py-3 border-b border-[var(--hairline)]" onClick={() => setMobileMenuOpen(false)}>
                {link.label}
              </Link>
            ))}
            <div className="flex items-center justify-between gap-4 pt-5">
              <button type="button" className="rail__lang" onClick={toggleLanguage}>
                {isEn ? 'العربية' : 'English'}
              </button>
              {isAuthenticated ? (
                <button type="button" className="btn-line" onClick={handleLogout}>{t('nav.logout')}</button>
              ) : (
                <div className="flex items-center gap-4">
                  <Link to="/login" className="rail__link" onClick={() => setMobileMenuOpen(false)}>{t('nav.login')}</Link>
                  <button type="button" className="btn-ink" onClick={() => navigate('/signup')}>{t('nav.signup')}</button>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>
    );
  }

  return (
    <nav className="border-b border-border/70 bg-background/80 backdrop-blur-xl sticky top-0 z-50">
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between gap-6">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 -my-6">
            <BrandLogo showTagline size="lg" className="transition-transform duration-200 hover:scale-105" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-sm font-medium text-muted-foreground hover:text-accent transition-colors"
              >
                {link.label}
              </Link>
            ))}
            {isSuperAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-accent transition-colors focus:outline-none">
                  Admin
                  <ChevronDown className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {adminLinks.map((link) => (
                    <DropdownMenuItem key={link.to} asChild>
                      <Link to={link.to}>{link.label}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleLanguage}
              title="Toggle Language"
              className="text-muted-foreground hover:text-primary"
            >
              <Globe className="h-5 w-5" />
            </Button>
            {isAuthenticated ? (
              <Button onClick={handleLogout} variant="outline">
                {t('nav.logout')}
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')} className="text-sm">
                  {t('nav.login')}
                </Button>
                <Button
                  onClick={() => navigate('/signup')}
                  className="shadow-lg shadow-accent/20 bg-accent text-accent-foreground hover:bg-accent/80"
                >
                  {t('nav.signup')}
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="block text-sm font-medium text-muted-foreground hover:text-accent"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            {isSuperAdmin && (
              <div className="space-y-2 pt-2 border-t">
                <p className="text-xs font-semibold uppercase text-muted-foreground/70">Admin</p>
                {adminLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="block text-sm font-medium text-muted-foreground hover:text-accent"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
            <div className="flex flex-col gap-3 pt-4 border-t">
              <Button variant="ghost" size="sm" onClick={toggleLanguage} className="flex-1">
                <Globe className="h-4 w-4 mr-2" />
                {i18n.language === 'en' ? 'العربية' : 'English'}
              </Button>
              {isAuthenticated ? (
                <Button onClick={handleLogout} variant="outline" size="sm" className="flex-1">
                  {t('nav.logout')}
                </Button>
              ) : (
                <div className="flex flex-col gap-2">
                  <Button onClick={() => navigate('/login')} size="sm" className="w-full" variant="outline">
                    {t('nav.login')}
                  </Button>
                  <Button
                    onClick={() => navigate('/signup')}
                    size="sm"
                    className="w-full bg-accent text-accent-foreground hover:bg-accent/80"
                  >
                    {t('nav.signup')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
